import { useMemo, useReducer } from 'react';

import { ELEMENT_NO_VALIDATION } from '../../../../../@types/forms/constants/ElementValidations';
import {
  ElementSettingsState,
  ElementSettingsAction,
  ElementSettingsActionType,
} from '../types/elementSettingsTypes';

/**
 * Creates initial state for element settings
 */
const createInitialState = (translationFunc: (key: string) => string): ElementSettingsState => ({
  selectedElement: null,
  elementTextStyle: null,
  elementTextDecoration: null,
  textRotation: null,
  selectedColor: null,
  options: [],
  formGroupOptions: [],
  customLists: [],
  selectedList: null,
  uploadedFile: null,
  staticText: undefined,
  daysOfCountry: {
    code: 'LT',
    label: translationFunc('shared.countries.lt'),
  },
  dateRangeTemplate: undefined,
  calendarEventType: null,
  todayDate: false,
  isPastDisabled: false,
  isOpenTextRotation: false,
  isOpenColor: false,
  openedTabIndex: 0,
  isElementRequired: false,
  validation: ELEMENT_NO_VALIDATION,
  hyperlink: { text: '', url: '' },
  userInfo: null,
  prefix: undefined,
  numberOfCharacters: [],
  selectedChildElement: null,
  childElements: [],
  sameTypeElementsList: [],
  isForExternal: false,
  isForInternal: false,
  isForAll: true,
});

/**
 * Reducer for element settings state management
 */
const elementSettingsReducer = (
  state: ElementSettingsState,
  action: ElementSettingsAction
): ElementSettingsState => {
  switch (action.type) {
    case ElementSettingsActionType.SET_SELECTED_ELEMENT:
      return {
        ...state,
        selectedElement: action.payload,
      };

    case ElementSettingsActionType.UPDATE_ELEMENT_FIELD:
      if (!state.selectedElement) return state;
      return {
        ...state,
        selectedElement: {
          ...state.selectedElement,
          ...action.payload,
        },
      };

    case ElementSettingsActionType.SET_TEXT_STYLE:
      return {
        ...state,
        elementTextStyle: action.payload,
      };

    case ElementSettingsActionType.SET_TEXT_DECORATION:
      return {
        ...state,
        elementTextDecoration: action.payload,
      };

    case ElementSettingsActionType.SET_TEXT_ROTATION:
      return {
        ...state,
        textRotation: action.payload,
      };

    case ElementSettingsActionType.SET_COLOR:
      return {
        ...state,
        selectedColor: action.payload,
      };

    case ElementSettingsActionType.SET_OPTIONS:
      return {
        ...state,
        options: action.payload,
      };

    case ElementSettingsActionType.ADD_OPTION:
      return {
        ...state,
        options: [...state.options, action.payload],
      };

    case ElementSettingsActionType.DELETE_OPTION:
      return {
        ...state,
        options: state.options.filter((_, index) => index !== action.payload),
      };

    case ElementSettingsActionType.UPDATE_OPTION:
      return {
        ...state,
        options: state.options.map((option, index) =>
          index === action.payload.index ? action.payload.value : option
        ),
      };

    case ElementSettingsActionType.SET_FORM_GROUP_OPTIONS:
      return {
        ...state,
        formGroupOptions: action.payload,
      };

    case ElementSettingsActionType.ADD_FORM_GROUP_OPTION:
      return {
        ...state,
        formGroupOptions: [...state.formGroupOptions, action.payload],
      };

    case ElementSettingsActionType.DELETE_FORM_GROUP_OPTION:
      return {
        ...state,
        formGroupOptions: state.formGroupOptions.filter((_, index) => index !== action.payload),
      };

    case ElementSettingsActionType.UPDATE_FORM_GROUP_OPTION:
      return {
        ...state,
        formGroupOptions: state.formGroupOptions.map((option, index) =>
          index === action.payload.index ? action.payload.value : option
        ),
      };

    case ElementSettingsActionType.SET_CUSTOM_LISTS:
      return {
        ...state,
        customLists: action.payload,
      };

    case ElementSettingsActionType.SET_SELECTED_LIST:
      return {
        ...state,
        selectedList: action.payload,
      };

    case ElementSettingsActionType.SET_UPLOADED_FILE:
      return {
        ...state,
        uploadedFile: action.payload,
      };

    case ElementSettingsActionType.SET_STATIC_TEXT:
      return {
        ...state,
        staticText: action.payload,
      };

    case ElementSettingsActionType.SET_DAYS_OF_COUNTRY:
      return {
        ...state,
        daysOfCountry: action.payload,
      };

    case ElementSettingsActionType.SET_DATE_RANGE_TEMPLATE:
      return {
        ...state,
        dateRangeTemplate: action.payload,
      };

    case ElementSettingsActionType.SET_CALENDAR_EVENT_TYPE:
      return {
        ...state,
        calendarEventType: action.payload,
      };

    case ElementSettingsActionType.SET_TODAY_DATE:
      return {
        ...state,
        todayDate: action.payload,
      };

    case ElementSettingsActionType.SET_PAST_DISABLED:
      return {
        ...state,
        isPastDisabled: action.payload,
      };

    case ElementSettingsActionType.TOGGLE_TEXT_ROTATION:
      return {
        ...state,
        isOpenTextRotation: !state.isOpenTextRotation,
        isOpenColor: false,
      };

    case ElementSettingsActionType.TOGGLE_COLOR:
      return {
        ...state,
        isOpenColor: !state.isOpenColor,
        isOpenTextRotation: false,
      };

    case ElementSettingsActionType.SET_TAB_INDEX:
      return {
        ...state,
        openedTabIndex: action.payload,
      };

    case ElementSettingsActionType.SET_ELEMENT_REQUIRED:
      return {
        ...state,
        isElementRequired: action.payload,
      };

    case ElementSettingsActionType.SET_VALIDATION:
      return {
        ...state,
        validation: action.payload,
      };

    case ElementSettingsActionType.SET_HYPERLINK:
      return {
        ...state,
        hyperlink: action.payload,
      };

    case ElementSettingsActionType.SET_USER_INFO:
      return {
        ...state,
        userInfo: action.payload,
      };

    case ElementSettingsActionType.SET_PREFIX:
      return {
        ...state,
        prefix: action.payload,
      };

    case ElementSettingsActionType.SET_NUMBER_OF_CHARACTERS:
      return {
        ...state,
        numberOfCharacters: action.payload,
      };

    case ElementSettingsActionType.SET_SELECTED_CHILD_ELEMENT:
      return {
        ...state,
        selectedChildElement: action.payload,
      };

    case ElementSettingsActionType.SET_CHILD_ELEMENTS:
      return {
        ...state,
        childElements: action.payload,
      };

    case ElementSettingsActionType.ADD_CHILD_ELEMENT:
      return {
        ...state,
        childElements: [...state.childElements, action.payload],
        selectedChildElement: null,
      };

    case ElementSettingsActionType.DELETE_CHILD_ELEMENT:
      return {
        ...state,
        childElements: state.childElements.filter((_, index) => index !== action.payload),
      };

    case ElementSettingsActionType.SET_SAME_TYPE_ELEMENTS_LIST:
      return {
        ...state,
        sameTypeElementsList: action.payload,
      };

    case ElementSettingsActionType.SET_ADDRESS_BOOK_TYPE:
      return {
        ...state,
        isForAll: action.payload.isForAll,
        isForExternal: action.payload.isForExternal,
        isForInternal: action.payload.isForInternal,
      };

    case ElementSettingsActionType.RESET_STATE:
      return createInitialState((key: string) => action.payload);

    case ElementSettingsActionType.INITIALIZE_FROM_ELEMENT:
      // Initialize state from element prop - reset UI state completely
      return {
        ...state,
        selectedElement: action.payload,

        // Reset UI state
        elementTextStyle: null, // Will be set by useElementSync
        elementTextDecoration: null, // Will be set by useElementSync
        isOpenTextRotation: false,
        isOpenColor: false,
        openedTabIndex: 0,

        // Initialize from element
        isElementRequired: action.payload.required || false,
        validation: action.payload.validations || ELEMENT_NO_VALIDATION,
        options: action.payload.options || [],
        formGroupOptions: action.payload.formGroupOptions || [],
        selectedList: action.payload.list || null,
        uploadedFile: action.payload.file || null,
        staticText: action.payload.staticText,
        daysOfCountry: action.payload.daysOfCountry || state.daysOfCountry,
        dateRangeTemplate: action.payload.dateRangeTemplate,
        calendarEventType: null, // Will be set by useElementSync based on ID
        todayDate: action.payload.todayDate || false,
        isPastDisabled: action.payload.disablePast || false,
        hyperlink: action.payload.hyperlink || { text: '', url: '' },
        userInfo: action.payload.userDataOption,
        numberOfCharacters: action.payload.numberOfCharacters || [],
        childElements: action.payload.childElements || [],
        prefix: action.payload.prefix,
        isForExternal: action.payload.isForExternal || false,
        isForInternal: action.payload.isForInternal || false,
        isForAll: action.payload.isForAll !== undefined ? action.payload.isForAll : true,
        textRotation: action.payload.rotate,
        selectedColor: action.payload.color,
      };

    default:
      return state;
  }
};

/**
 * Custom hook for managing element settings state
 */
export const useElementState = (translationFunc: (key: string) => string) => {
  const initialState = useMemo(() => createInitialState(translationFunc), [translationFunc]);

  const [state, dispatch] = useReducer(elementSettingsReducer, initialState);

  return { state, dispatch };
};
